import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { my_routes } from "../router/all_routes";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { createUser, getAllDepartment } from "../../api/users";

// pour restreaindre l'accès à ce composant
import {
  Action,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";

// Schema de notre formulaire de création de compte
const schema = z.object({
  lastname: z.string().nonempty("Veuillez insérer un nom"),
  firstname: z.string().nonempty("Veuillez insérer un prénom"),
  sexe: z.string().nonempty("Veuillez sélectionner le sexe"),
  email: z.string().email("Veuillez fournir une adresse email valide"),
  number: z
    .string()
    .regex(
      /^(\+223)?[456789]\d{7}$/,
      "Numéro de téléphone invalide. Ex: +22370123456 ou 70123456",
    )
    .nonempty("Veuillez insérer un numéro de téléphone"),
  id_department: z.string().nonempty("Le département est requis"),
  jobFunction: z.string().nonempty("Veuillez insérer une fonction"),
  birthday: z.string({ message: "Veuillez insérer une date de naissance" }),
  adresse: z.string().nonempty("Veuillez insérer une adresse"),
  statut: z.enum(["true", "false"], {
    required_error: "Veuillez sélectionner un statut",
  }),
});

type FormFields = z.infer<typeof schema>;

interface Department {
  id: string;
  label: string;
}

const CreateUser = () => {
  const navigate = useNavigate();
  const routes = my_routes;

  const [departments, setDepartments] = useState<Department[]>([]);

  //Initialisation de zod sur notre formulaire de création de compte
  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(schema),
  });

  //Traitement après validation du formulaire de création de compte
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      //On convertit le champ statut qui n'arrive pas à être envoyé en string
      const userData = {
        ...data,
        statut: data.statut === "true", // conversion string → boolean
      };

      //Envoi des datas pour création vers le backend
      // console.log(userData);
      await createUser(userData);

      //On réinitialise les champs du formulaire
      reset();

      //On rédirige vers la liste des utilisateurs
      navigate(routes.allUser, {
        state: { successMessage: "Utilisateur créé avec succès." },
      });
    } catch (error: any) {
      // Si erreur
      // console.log(error);
      setError("root", {
        message:
          error.response.data.error ||
          error.response.data.message ||
          "Une erreur inattendu c'est produite veuillez ésssyez plus tard",
      });
    }
  };

  //On recupère la liste des départements en appelant sa fonction
  useEffect(() => {
    getAllDepartment(buildPermissionContext(Resource.USER, Action.CREATE)).then(
      (departments) => {
        // console.log(departments.data)
        setDepartments(departments.data);
      },
    );
  }, []);

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">
                Ajouter un Personnel Administratif
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.allUser}>Compte</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Personnel Administratif
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          {/* /Page Header */}

          {/* Message d'erreur serveur */}
          {errors.root && (
            <div className="alert alert-danger rounded-pill alert-dismissible fade show">
              {errors.root.message}
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="alert"
                aria-label="Close"
              >
                <i className="fas fa-xmark" />
              </button>
            </div>
          )}

          <div className="row">
            <div className="col-md-12">
              <form
                className="needs-validation"
                onSubmit={handleSubmit(onSubmit)}
              >
                <>
                  {/* Personal Information */}
                  <div className="card">
                    <div className="card-header bg-light">
                      <div className="d-flex align-items-center">
                        <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                          <i className="ti ti-info-square-rounded fs-16" />
                        </span>
                        <h4 className="text-dark">
                          {" "}
                          Informations Personnelles
                        </h4>
                      </div>
                    </div>
                    <div className="card-body pb-1">
                      <div className="row row-cols-xxl-5 row-cols-md-6">
                        <div className="col-xxl-4 col-xl-4 col-md-6">
                          <div className="mb-3">
                            <label
                              className="form-label"
                              htmlFor="validationServer01"
                            >
                              {" "}
                              Nom
                            </label>
                            <input
                              {...register("lastname")}
                              id="validationServer01"
                              type="text"
                              className={
                                errors.lastname
                                  ? "form-control is-invalid"
                                  : "form-control"
                              }
                            />
                            {errors.lastname && (
                              <div className="invalid-feedback">
                                {errors.lastname.message}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xxl-4 col-xl-4 col-md-6">
                          <div className="mb-3">
                            <label className="form-label"> Prénom</label>
                            <input
                              {...register("firstname")}
                              type="text"
                              className={
                                errors.firstname
                                  ? "form-control is-invalid"
                                  : "form-control"
                              }
                            />
                            {errors.firstname && (
                              <div className="invalid-feedback">
                                {errors.firstname.message}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xxl-4 col-xl-4 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Sexe</label>
                            <select
                              {...register("sexe")}
                              aria-label="Default select example"
                              className={
                                errors.sexe
                                  ? "form-select is-invalid"
                                  : "form-select"
                              }
                            >
                              <option value={""}>Sélectionner</option>
                              <option value={"Masculin"}>Homme</option>
                              <option value={"Feminin"}>Femme</option>
                            </select>
                            {errors.sexe && (
                              <div className="invalid-feedback">
                                {errors.sexe.message}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xxl-4 col-xl-4 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Addresse Email</label>
                            <input
                              {...register("email")}
                              type="text"
                              className={
                                errors.email
                                  ? "form-control is-invalid"
                                  : "form-control"
                              }
                            />
                            {errors.email && (
                              <div className="invalid-feedback">
                                {errors.email.message}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xxl-4 col-xl-4 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Contact Mobile/Fixe
                            </label>
                            <input
                              {...register("number")}
                              type="text"
                              className={
                                errors.number
                                  ? "form-control is-invalid"
                                  : "form-control"
                              }
                            />
                            {errors.number && (
                              <div className="invalid-feedback">
                                {errors.number.message}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xxl-4 col-xl-4 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Date de Naissance
                            </label>
                            <div className="input-icon position-relative">
                              <input
                                type="date"
                                {...register("birthday")}
                                placeholder="DD-MM-YYYY"
                                className={
                                  errors.birthday
                                    ? "form-control datetimepicker is-invalid"
                                    : "form-control datetimepicker"
                                }
                              />
                              {errors.birthday && (
                                <div className="invalid-feedback">
                                  {errors.birthday.message}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-xxl-4 col-xl-4 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Département</label>
                            <select
                              {...register("id_department")}
                              aria-label="Default select example"
                              className={
                                errors.id_department
                                  ? "form-select is-invalid"
                                  : "form-select"
                              }
                            >
                              <option value={""}>Sélectionner</option>
                              {departments.map((department) => (
                                <option
                                  key={department.id}
                                  value={department.id}
                                >
                                  {department.label}
                                </option>
                              ))}
                            </select>
                            {errors.id_department && (
                              <div className="invalid-feedback">
                                {errors.id_department.message}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-xxl-4 col-xl-4 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              {" "}
                              Fonction (Poste)
                            </label>
                            <input
                              {...register("jobFunction")}
                              type="text"
                              className={
                                errors.jobFunction
                                  ? "form-control is-invalid"
                                  : "form-control"
                              }
                            />
                            {errors.jobFunction && (
                              <div className="invalid-feedback">
                                {errors.jobFunction.message}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-xxl-12 col-md-12 col-xl-12">
                          <div className="mb-3">
                            <label className="form-label">
                              Adresse Physique
                            </label>
                            <textarea
                              {...register("adresse")}
                              placeholder="Exemple : Badalabougou, Commune 5, Rue 300 Porte 218"
                              rows={4}
                              className={
                                errors.adresse
                                  ? "form-control is-invalid"
                                  : "form-control"
                              }
                            />
                            {errors.adresse && (
                              <div className="invalid-feedback">
                                {errors.adresse.message}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-xxl-3 col-xl-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Status</label>
                            <select
                              {...register("statut")}
                              aria-label="Default select example"
                              className={
                                errors.statut
                                  ? "form-select is-invalid"
                                  : "form-select"
                              }
                            >
                              <option value={""}>Sélectionner</option>
                              <option value={"true"}>Actif</option>
                              <option value={"false"}>Inactif</option>
                            </select>
                            {errors.statut && (
                              <div className="invalid-feedback">
                                {errors.statut.message}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* /Personal Information */}
                </>

                <div className="text-end">
                  <button type="button" className="btn btn-light me-3">
                    <Link to={routes.allUser}>Annuler</Link>
                  </button>

                  {isSubmitting ? (
                    <button
                      className="btn btn-primary-light"
                      type="button"
                      disabled={false}
                    >
                      <span
                        className="spinner-border spinner-border-sm align-middle me-1"
                        role="status"
                        aria-hidden="true"
                      />
                      Chargement...
                    </button>
                  ) : (
                    <button
                      disabled={isSubmitting}
                      type="submit"
                      className="btn btn-primary"
                    >
                      Enregistrer
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// export default withPermission(CreateUser,"user:create","accès-refuser")
export default CreateUser;
